import { IExternalCalenderEvent } from "@app-types/api/externalCalendar.types";

import axios, { AxiosResponse } from "axios";

export function parseICSFromURL(url: string, useProxy: boolean, validateCalendar = false): Promise<IExternalCalenderEvent[]> {
    const normalized = url.replace(/^webcal:\/\//i, "https://");
    const fetchUrl = "https://utils.varannanvecka.app/proxy?url=" + encodeURIComponent(normalized)

    const instance = axios.create();
    /* tslint:disable:no-string-literal */
    delete instance.defaults.headers.common['Authorization']; // no auth -> no preflight
    /* tslint:enable:no-string-literal */

    return instance
        .get<string>(fetchUrl, { timeout: 15000 })
        .then((response: AxiosResponse<string>) => {
            if (validateCalendar && (typeof response.data !== "string"
                || !/(?:^|\r?\n)BEGIN:VCALENDAR\s*(?:\r?\n)/i.test(response.data.trim())
                || !/(?:^|\r?\n)END:VCALENDAR\s*$/i.test(response.data.trim()))) {
                throw new Error("Invalid calendar feed");
            }
            if (response.data) { return parseICS(response.data) }
            return []
        }).catch((error) => {
            if (validateCalendar) { throw error; }
            console.error("Error fetching ICS file via axios:", error);
            return [];
        });
}

export function parseICS(icsContent: string): IExternalCalenderEvent[] {
    const events: IExternalCalenderEvent[] = [];
    const lines = icsContent.split(/\r?\n/);

    let currentEvent: Partial<IExternalCalenderEvent> | null = null;
    let inEvent = false;

    const uniqueUIDs = new Set<string>();
    for (let i = 0; i < lines.length; i++) {
        let line = lines[i];

        // Hantera radbrytningar
        while (
            i + 1 < lines.length &&
            (lines[i + 1].startsWith(' ') || lines[i + 1].startsWith('\t'))
        ) {
            line += lines[i + 1].substr(1);
            i++;
        }


        if (line === 'BEGIN:VEVENT') {
            inEvent = true;
            currentEvent = {};
        } else if (line === 'END:VEVENT') {

            if (currentEvent !== null) {
                if (currentEvent.endTime === undefined && currentEvent.startTime !== undefined) {
                    currentEvent.endTime = currentEvent.startTime + 300000;
                }
                if (currentEvent.description === undefined) {
                    currentEvent.description = "";
                }
            }
            if (
                currentEvent &&
                !isNaN(currentEvent.startTime!) &&
                !isNaN(currentEvent.endTime!) &&
                currentEvent.title !== undefined &&
                currentEvent.description !== undefined &&
                currentEvent.id !== undefined
            ) {
                if (!uniqueUIDs.has(currentEvent.id)) {
                    events.push(currentEvent as IExternalCalenderEvent);
                    uniqueUIDs.add(currentEvent.id);
                } else {
                    console.log("Duplicate event with UID", currentEvent.id, "detected, skipping.");
                }
            } else {
                console.log("Event is missing required fields, skipping:", currentEvent);
            }
            inEvent = false;
            currentEvent = null;
        } else if (inEvent && currentEvent) {

            // example line = DTSTART;TZID=W. Europe Standard Time:20250928T090000

            const colonIndex = line.indexOf(':');
            if (colonIndex === -1) {
                continue; // Felaktig rad, hoppa över
            }
            const [keyPart, value] = [
                line.substring(0, colonIndex),
                line.substring(colonIndex + 1),
            ];

            // Split the key part to separate the key from any parameters
            const segments = keyPart.split(';');
            const key = segments[0].toUpperCase();
            let tzid: string | undefined;

            // Look for a TZID parameter among the segments
            for (const segment of segments) {
                if (segment.toUpperCase().startsWith("TZID=")) {
                    tzid = segment.substring(5);
                    break;
                }
            }

            switch (key) {
                case 'UID':
                    currentEvent.id = value;
                    break;
                case 'SUMMARY':
                    currentEvent.title = unescapeICSString(value);
                    break;
                case 'DESCRIPTION':
                    currentEvent.description = unescapeICSString(value);
                    break;
                case 'DTSTART':
                    currentEvent.startTime = parseICSDatetime(value, tzid);
                    break;
                case 'DTEND':
                    currentEvent.endTime = parseICSDatetime(value, tzid);
                    break;
                default:
                    break;
            }
        }
    }

    return events;
}

function parseICSDatetime(value: string, tzid?: string): number {
    // Handle UTC and local times. If a timezone identifier is provided, use it.
    if (value.endsWith('Z')) {
        const dateString = value.substring(0, value.length - 1);
        return parseDateString(dateString, true);
    } else if (tzid) {
        // Pass the timezone info to parseDateString (adjust parseDateString accordingly)
        return parseDateString(value, false, tzid);
    } else {
        return parseDateString(value, false);
    }
}

function parseDateString(dateString: string, isUTC: boolean, tzid?: string): number {
    try {
        const year = parseInt(dateString.substr(0, 4), 10);
        const month = parseInt(dateString.substr(4, 2), 10) - 1;
        const day = parseInt(dateString.substr(6, 2), 10);
        let hour = 0;
        let minute = 0;
        let second = 0;

        if (dateString.length >= 15) {
            hour = parseInt(dateString.substr(9, 2), 10);
            minute = parseInt(dateString.substr(11, 2), 10);
            second = parseInt(dateString.substr(13, 2), 10);
        } else if (dateString.length !== 8) {
            return NaN;
        }

        if (isUTC) {
            return Date.UTC(year, month, day, hour, minute, second);
        } else if (tzid) {
            // Convert Windows TZ names to IANA names if necessary.
            const ianaTz = normalizeTzid(tzid);

            // The input components represent the local time in the target timezone.
            // First, assume those components are UTC.
            const assumedUTC = Date.UTC(year, month, day, hour, minute, second);
            const assumedDate = new Date(assumedUTC);

            // Get the offset (in minutes) for the target timezone at that moment.
            const offsetMinutes = getOffset(ianaTz, assumedDate);

            // Adjust the assumed UTC time by subtracting the offset.
            return assumedUTC - offsetMinutes * 60000;
        } else {
            // Without a tzid, interpret using the system’s local timezone.
            return new Date(year, month, day, hour, minute, second).getTime();
        }
    } catch (e) {
        return NaN;
    }
}

/**
 * Converts Windows time zone identifiers to IANA identifiers.
 * If the tzid is already in IANA format (contains a slash), it is returned as-is.
 */
function normalizeTzid(tzid: string): string {
    const windowsToIana: { [key: string]: string } = {
        "Dateline Standard Time": "Etc/GMT+12",
        "UTC-11": "Etc/GMT+11",
        "Aleutian Standard Time": "America/Adak",
        "Hawaiian Standard Time": "Pacific/Honolulu",
        "Alaskan Standard Time": "America/Anchorage",
        "Pacific Standard Time (Mexico)": "America/Tijuana",
        "Pacific Standard Time": "America/Los_Angeles",
        "US Mountain Standard Time": "America/Phoenix",
        "Mountain Standard Time (Mexico)": "America/Chihuahua",
        "Mountain Standard Time": "America/Denver",
        "Central America Standard Time": "America/Guatemala",
        "Central Standard Time": "America/Chicago",
        "Central Standard Time (Mexico)": "America/Mexico_City",
        "Canada Central Standard Time": "America/Regina",
        "SA Pacific Standard Time": "America/Bogota",
        "Eastern Standard Time": "America/New_York",
        "US Eastern Standard Time": "America/Indianapolis",
        "SA Western Standard Time": "America/La_Paz",
        "Pacific SA Standard Time": "America/Santiago",
        "Newfoundland Standard Time": "America/St_Johns",
        "E. South America Standard Time": "America/Sao_Paulo",
        "Argentina Standard Time": "America/Buenos_Aires",
        "SA Eastern Standard Time": "America/Cayenne",
        "Greenland Standard Time": "America/Godthab",
        "Montevideo Standard Time": "America/Montevideo",
        "Bahia Standard Time": "America/Bahia",
        "UTC": "Etc/UTC",
        "GMT Standard Time": "Europe/London",
        "Greenwich Standard Time": "Atlantic/Reykjavik",
        "W. Europe Standard Time": "Europe/Berlin",
        "Central Europe Standard Time": "Europe/Budapest",
        "Romance Standard Time": "Europe/Paris",
        "Central European Standard Time": "Europe/Warsaw",
        "W. Central Africa Standard Time": "Africa/Lagos",
        "Namibia Standard Time": "Africa/Windhoek",
        "Jordan Standard Time": "Asia/Amman",
        "GTB Standard Time": "Europe/Bucharest",
        "Middle East Standard Time": "Asia/Beirut",
        "Egypt Standard Time": "Africa/Cairo",
        "E. Europe Standard Time": "Europe/Chisinau",
        "Syria Standard Time": "Asia/Damascus",
        "West Bank Standard Time": "Asia/Hebron",
        "South Africa Standard Time": "Africa/Johannesburg",
        "FLE Standard Time": "Europe/Kiev", // alternatively Europe/Helsinki
        "Turkey Standard Time": "Europe/Istanbul",
        "Israel Standard Time": "Asia/Jerusalem",
        "Arabic Standard Time": "Asia/Baghdad",
        "Arab Standard Time": "Asia/Riyadh",
        "E. Africa Standard Time": "Africa/Nairobi",
        "Iran Standard Time": "Asia/Tehran",
        "Arabian Standard Time": "Asia/Dubai",
        "Azerbaijan Standard Time": "Asia/Baku",
        "Russia Time Zone 3": "Europe/Samara",
        "Mauritius Standard Time": "Indian/Mauritius",
        "Saratov Standard Time": "Europe/Saratov",
        "Georgian Standard Time": "Asia/Tbilisi",
        "Caucasus Standard Time": "Asia/Yerevan",
        "Afghanistan Standard Time": "Asia/Kabul",
        "West Asia Standard Time": "Asia/Tashkent",
        "Ekaterinburg Standard Time": "Asia/Yekaterinburg",
        "Pakistan Standard Time": "Asia/Karachi",
        "India Standard Time": "Asia/Kolkata",
        "Sri Lanka Standard Time": "Asia/Colombo",
        "Nepal Standard Time": "Asia/Kathmandu",
        "Central Asia Standard Time": "Asia/Almaty",
        "Bangladesh Standard Time": "Asia/Dhaka",
        "Myanmar Standard Time": "Asia/Yangon",
        "SE Asia Standard Time": "Asia/Bangkok",
        "North Asia Standard Time": "Asia/Krasnoyarsk",
        "China Standard Time": "Asia/Shanghai",
        "North Asia East Standard Time": "Asia/Irkutsk",
        "Singapore Standard Time": "Asia/Singapore",
        "W. Australia Standard Time": "Australia/Perth",
        "Taipei Standard Time": "Asia/Taipei",
        "Ulaanbaatar Standard Time": "Asia/Ulaanbaatar",
        "Tokyo Standard Time": "Asia/Tokyo",
        "Korea Standard Time": "Asia/Seoul",
        "Yakutsk Standard Time": "Asia/Yakutsk",
        "Cen. Australia Standard Time": "Australia/Adelaide",
        "AUS Central Standard Time": "Australia/Darwin",
        "E. Australia Standard Time": "Australia/Brisbane",
        "AUS Eastern Standard Time": "Australia/Sydney",
        "West Pacific Standard Time": "Pacific/Port_Moresby",
        "Tasmania Standard Time": "Australia/Hobart",
        "Magadan Standard Time": "Asia/Magadan",
        "Vladivostok Standard Time": "Asia/Vladivostok",
        "Russia Time Zone 10": "Asia/Srednekolymsk",
        "Central Pacific Standard Time": "Pacific/Guadalcanal",
        "Russia Time Zone 11": "Asia/Kamchatka",
        "New Zealand Standard Time": "Pacific/Auckland",
        "UTC+12": "Etc/GMT-12",
        "Fiji Standard Time": "Pacific/Fiji",
        "Tonga Standard Time": "Pacific/Tongatapu",
        "Samoa Standard Time": "Pacific/Apia"
    };

    // If tzid already contains a slash, assume it's a valid IANA timezone.
    if (tzid.indexOf("/") !== -1) {
        return tzid;
    }

    // Otherwise, use the mapping.
    return windowsToIana[tzid] || tzid;
}
  

/**
 * Computes the timezone offset (in minutes) for a given IANA timezone at the moment
 * represented by `date`. The technique uses Intl.DateTimeFormat to obtain the local
 * time parts for that zone, then reconstructs a UTC timestamp.
 *
 * For example, if the target zone is 2 hours ahead of UTC at that time,
 * the function returns +120.
 */
function getOffset(timeZone: string, date: Date): number {
    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

    const parts = dtf.formatToParts(date);
    const data: { [key: string]: number } = {};
    for (const part of parts) {
        if (part.type !== "literal") {
            data[part.type] = parseInt(part.value, 10);
        }
    }

    // Interpret the parts as if they were UTC.
    const asUTC = Date.UTC(data.year, data.month - 1, data.day, data.hour, data.minute, data.second);

    // The difference (in minutes) between the target zone’s local time and the given UTC date.
    // (A positive value means the zone is ahead of UTC.)
    return (asUTC - date.getTime()) / 60000;
}



function unescapeICSString(value: string): string {
    return value
        .replace(/\\,/g, ',') // Hantera escape:at kommatecken
        .replace(/\\\\/g, '\\') // Hantera dubbla backslashes
        .replace(/\\n/g, '\n') // Hantera nya rader
        .replace(/\\;/g, ';'); // Hantera escape:at semikolon
}



// WEBPACK FOOTER //
// ./src/helpers/parseICS.ts